// Revise ▸ Checks (the Mac's ChecksPage, ChecksColumn, ReviseReader, FindingsColumn and FindingCard;
// `design-docs/Revise passes — design.md`): the manuscript read in passes — Mistakes, Typography, Consistency, Style —
// from the certain to the arguable. The page shows a chapter at a time (`reviseUnits`) as it reads, in the book's face,
// with what the checks found as dotted underlines in their pass's ink (verdigris for the writer's habits), faint for the
// passes the margin isn't working on. The margin lists the findings of the pass worked on, chapter by chapter, for the
// whole manuscript (`reviseBook`, worked out in the Revise worker after each change), with ‹ › to step through them; the
// chosen one's card says what caught the checker's eye: Fix (and Fix All … Like This for typography), the forms of a
// consistency set to choose between, Add to margin, Leave it (this device: `dismissedFindings-<project>`), Open in
// Write. On a phone the pass, the stepper and the card rise from the foot.
//
// The column: each pass with its count (✓ when nothing's left) — choosing one sets the margin to it — and each check with
// a tick and how many passages it marks. A built-in style check's tick switches it for the project (kept with it, as on the
// Mac); the others switch on this device (`reviseHiddenChecks`, and `reviseShownChecks` for underlining, off by default).
// Typography's Fix All, checking the style, and the style sheet (dialogue marks, dashes, numbers and the consistency
// choices) are there too. Spelling is checked in English against SCOWL's dictionaries (`Speller`, in the Revise worker),
// other languages on the Mac and iPad; the writer's own habits are checked on the Mac and iPad. The column says so.
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { plainText, utf16OffsetOfScalar, type ArrangementTreeNode, type OpenedProject } from '@writing-suite/core';
import {
  checkName, checkPass, checkShortName, checkSummary, checksIn, COLUMN_PASSES, DASH_STYLES, dashStyleExample, dashStyleName, findingPass, fixesAllAtOnce,
  isBuiltInStyleCheck, isCheckOn, MISTAKE_CHECKS, PERSONAL_RULES_APPLE_ONLY, projectStyleSheet, readingParagraphs, reviseScenes, reviseUnit, reviseUnits,
  revisePassName, SPELLING_CHECK, spellingDictionary, styleCheckIsExact, styleSettings, TYPOGRAPHY_CHECKS, variantChoosesAForm, variantKindName,
  type BuiltInStyleCheck, type DashStyle, type NumberStyle, type ReviseFinding, type RevisePass, type ReviseScene, type VariantChoice, type VariantSet,
} from '@writing-suite/core/revise';
import { markdown } from '@writing-suite/editor';
import type { PageProps } from '../../shell/ProjectShell.tsx';
import { announce } from '../../shell/context.ts';
import { useWorkspace } from '../../shell/workspace.ts';
import { followLink, navigate } from '../../shell/route.ts';
import {
  addToMargin, addWordToGlossary, askFixAll, bookNow, cancelStyleCheck, chooseFinding, chooseNext, chosenPass, fixAll, fixAllOf, fixAllTypography, fixFinding, leaveFinding,
  neighbouringFinding, refreshBook, removeStyleChoice, setFindingStepper, revisePassOf, reviseSwitches, runStyleCheck, setBuiltInStyleCheck, setChosenPass, setDashStyle,
  setDialogueQuotes, setNumberStyle, showStyleSheet, styleSheetDocument, shownFindings, toggleCheck, useBookState, useRevisePrefs, useStyleRun, applyVariant,
  type BookState, type FixAllRequest,
} from '../../store/revise.ts';
import { MenuButton } from '../Menu.tsx';
import { downloadText } from '../../store/story.ts';

/** What the web's spelling check is (beside the check, and on its card). */
export const SPELLING_NOTE = 'On the web, words are checked against SCOWL’s English dictionaries (American, Canadian, or Australian for other English), so it may not know every word your Mac does. Your own dictionary is on your Mac and iPad.';

/** Checks that are approximate in the browser, and why (shown beside them). */
export const APPROXIMATE_NOTE = 'Approximate on the web: finding word forms and parts of speech needs Apple’s language tagger, so the browser estimates them. Your Mac and iPad check exactly.';

const opened = (p: PageProps['project']) => p as OpenedProject;

/** "Fourteen · The Weir": the chapter's number word and title, else its title. */
export function unitTitle(project: OpenedProject, unit: ArrangementTreeNode): string {
  const name = project.nodeTitle(unit.node);
  const opener = unit.node.fragmentID !== undefined ? project.openers().get(unit.node.fragmentID) : undefined;
  const number = opener?.label?.split(' ').slice(1).join(' ');
  return number ? `${number} · ${name}` : name;
}

/** A finding's pass, as the class that gives it its ink (`pass-<pass>`; a habit's is `pass-rule`). */
const inkClass = (f: ReviseFinding) => (f.check.startsWith('rule:') ? 'pass-rule' : `pass-${findingPass(f)}`);
const eyebrow = (f: ReviseFinding) => (f.check.startsWith('rule:') ? `Your habit: ${f.checkName}` : f.checkName);
const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

// MARK: The book

/** The whole manuscript's findings (worked out again in the Revise worker shortly after each change; in this thread when
 *  there's no live project), the device's choices, and what's shown. Shared by the page, its column and its header. */
export function useChecks({ project, live }: Pick<PageProps, 'project' | 'live'>) {
  const prefs = useRevisePrefs();
  const run = useStyleRun(project.id);
  const state = useBookState(project.id);
  const p = opened(project);
  useEffect(() => {
    if (!live) return;
    const t = setTimeout(() => void refreshBook(live, p), state.book ? 300 : 0);
    return () => clearTimeout(t);
  }, [p, live, run.findings]); // eslint-disable-line react-hooks/exhaustive-deps
  const offline = useMemo(() => (live ? null : bookNow(p, run.findings)), [live, p, run.findings]);
  const book: BookState['book'] = offline ?? state.book;
  const all = useMemo(() => shownFindings(book, p), [book, p, prefs]); // eslint-disable-line react-hooks/exhaustive-deps
  const pass = revisePassOf(chosenPass(), all);
  const findings = pass ? all.filter((f) => findingPass(f) === pass) : all;
  return { p, book, loaded: book !== null, all, pass, findings, state, run };
}

// MARK: The page

export function ChecksPage(props: PageProps) {
  const { project, live, params, hrefFor } = props;
  const ws = useWorkspace();
  const { p, book, loaded, all, pass, findings, state, run } = useChecks(props);
  const units = useMemo(() => reviseUnits(p), [p]);
  const unit = reviseUnit(units, params.unit ?? null);
  const narrow = useNarrow('(max-width: 700px)');
  const canWrite = !!ws?.canWrite && !!live?.writable;
  const chosen = all.find((f) => f.id === state.selected) ?? null;
  // Project ▸ Check Style (⇧⌘L) lands here with ?check=1.
  const checked = useRef(false);
  useEffect(() => {
    if (params.check !== '1' || checked.current || !live) return;
    checked.current = true;
    void runStyleCheck(live, p);
    navigate(hrefFor('revise/checks', null, params.unit ? { unit: params.unit } : {}), { replace: true });
  }, [params.check, live]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Chooses a finding from the margin or the stepper: its chapter is the one read, scrolled to it. */
  const choose = (f: ReviseFinding) => {
    chooseFinding(project.id, f.id);
    if (unit && f.unitID && f.unitID !== unit.node.id) navigate(hrefFor('revise/checks', null, { unit: f.unitID }), { replace: true });
  };
  const step = (forward: boolean) => {
    const next = neighbouringFinding(book, findings, state.selected, forward, unit?.node.id ?? null);
    if (next) choose(next);
  };
  // Project ▸ Next Finding / Previous Finding step while the page is shown.
  const stepRef = useRef(step);
  stepRef.current = step;
  useEffect(() => {
    setFindingStepper((forward) => stepRef.current(forward));
    return () => setFindingStepper(null);
  }, []);
  // A finding chosen in another chapter (Next, or the next one after a fix) brings its chapter.
  useEffect(() => {
    if (chosen?.unitID && unit && chosen.unitID !== unit.node.id) navigate(hrefFor('revise/checks', null, { unit: chosen.unitID }), { replace: true });
  }, [chosen?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!unit) {
    return (
      <div className="checks-page">
        <header className="page-head">
          <h1 tabIndex={-1}>Checks</h1>
        </header>
        <div className="page-message">
          <h2>Nothing to revise yet</h2>
          <p className="lede">Write something in the manuscript first.</p>
        </div>
      </div>
    );
  }
  const title = unitTitle(p, unit);
  const here = all.filter((f) => f.unitID === unit.node.id);
  const scenes = book?.scenes[unit.node.id] ?? reviseScenes(p, unit);
  const card = (f: ReviseFinding, onClose?: () => void) => (
    <FindingCard key={f.id} {...props} finding={f} shown={all} variants={book?.variants ?? {}} canWrite={canWrite} onClose={onClose} />
  );
  const footCard = narrow && chosen && findings.some((f) => f.id === chosen.id) ? chosen : null;
  return (
    <div
      className={`checks-page${footCard ? ' with-card' : ''}${narrow ? ' narrow' : ''}`}
      data-unit={unit.node.id}
      data-findings={here.length}
      data-loaded={loaded ? 'true' : 'false'}
      data-pass={pass ?? 'all'}
    >
      <h1 tabIndex={-1} className="visually-hidden">
        Checks: {title}
      </h1>
      <div className="checks-layout">
        <ReviseReader
          unitID={unit.node.id}
          title={title}
          scenes={scenes}
          findings={here}
          focus={pass}
          selected={state.selected}
          reveal={state.reveal}
          loading={!loaded}
          onSelect={(id) => chooseFinding(project.id, id, false)}
        />
        {narrow ? (
          <div className="finding-foot">
            {footCard ? card(footCard, () => chooseFinding(project.id, null)) : null}
            <div className={`finding-foot-pill${pass ? ` pass-${pass}` : ''}`}>
              <PassMenu all={all} pass={pass} />
              {findings.length ? <FindingStepper findings={findings} selected={state.selected} onStep={step} /> : null}
            </div>
          </div>
        ) : (
          <FindingsColumn
            findings={findings}
            all={all}
            pass={pass}
            loaded={loaded}
            styleChecked={run.findings !== null}
            selected={state.selected}
            reveal={state.reveal}
            chapterTitle={(id) => {
              const u = units.find((x) => x.node.id === id);
              return u ? unitTitle(p, u) : '';
            }}
            onChoose={choose}
            onStep={step}
            card={(f) => card(f)}
          />
        )}
      </div>
      {state.fixAll ? <FixAllDialog {...props} request={state.fixAll} canWrite={canWrite} /> : null}
      {state.styleSheet ? <StyleSheetDialog {...props} canWrite={canWrite} /> : null}
    </div>
  );
}

/** The chapter as it reads, with findings as dotted underlines in their pass's ink; the chosen one washed. */
function ReviseReader({ unitID, title, scenes, findings, focus, selected, reveal, loading, onSelect }: {
  unitID: string; title: string; scenes: ReviseScene[]; findings: ReviseFinding[]; focus: RevisePass | null; selected: string | null; reveal: number;
  loading: boolean; onSelect(id: string): void;
}) {
  const rendered = useMemo(() => {
    const out = new Map<string, { kind: string; text: string; marks: { id: string; range: [number, number] }[]; alignment: string | null }[]>();
    for (const scene of scenes) {
      const marks = findings.filter((f) => f.fragmentID === scene.id).map((f) => ({ id: f.id, range: f.range }));
      let fence: string | null = null;
      const paragraphs: { kind: string; text: string; marks: { id: string; range: [number, number] }[]; alignment: string | null }[] = [];
      for (const para of readingParagraphs(scene.text, marks)) {
        const line = para.text.trim();
        if (line === ':::') { fence = null; continue; }
        if (line.startsWith('::: ')) { fence = line.slice(4); continue; }
        paragraphs.push({ kind: para.kind, text: para.text, marks: para.marks, alignment: fence });
      }
      out.set(scene.id, paragraphs);
    }
    return out;
  }, [scenes, findings]);
  const byId = useMemo(() => new Map(findings.map((f) => [f.id, f])), [findings]);
  const ref = useRef<HTMLElement>(null);
  const shownUnit = useRef<string | null>(null);
  // A finding chosen in the margin or stepped to is brought into view; another chapter opens at its top. Underlines
  // chosen in the text leave the page where it is.
  useEffect(() => {
    const newChapter = shownUnit.current !== unitID;
    shownUnit.current = unitID;
    const target = selected ? ref.current?.querySelector(`a.finding[data-finding="${CSS.escape(selected)}"]`) : null;
    const smooth = !newChapter && !matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (target) target.scrollIntoView({ block: 'center', behavior: smooth ? 'smooth' : 'auto' });
    else if (newChapter) ref.current?.scrollIntoView({ block: 'start' });
  }, [unitID, reveal, rendered]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <article ref={ref} className="revise-reader" aria-label={`Reading ${title}`}>
      <p className="eyebrow revise-eyebrow">{title}</p>
      {scenes.map((scene) => (
        <section key={scene.id} className="revise-scene" data-fragment-id={scene.id} aria-label={scene.title || 'Untitled'}>
          {scenes.length > 1 && scene.title ? <h2 className="revise-scene-title">{scene.title}</h2> : null}
          {(rendered.get(scene.id) ?? []).map((para, i) =>
            para.kind === 'sceneBreak' ? (
              <p key={i} className="revise-break" aria-label="Scene break">
                ✦
              </p>
            ) : (
              <p key={i} className={`revise-paragraph${para.kind === 'quotation' ? ' quotation' : ''}${para.alignment ? ` align-${para.alignment}` : ''}`}>
                {styledParagraph(para.text, para.marks, byId, selected, focus, onSelect)}
              </p>
            ),
          )}
        </section>
      ))}
      {loading ? <p className="revise-loading" role="status">Reading the manuscript…</p> : scenes.length === 0 ? <p className="empty-note">This chapter has no text yet.</p> : null}
    </article>
  );
}

/** A paragraph's Markdown styled as Write shows it (emphasis, small caps, underline, the writer's inks; markers faint,
 *  hidden syntax gone), each finding a link; `marked`, when given, styles a range (UTF-16) instead (the margin's rows). */
function styledParagraph(text: string, marks: { id: string; range: [number, number] }[], byId: Map<string, ReviseFinding>, selected: string | null,
  focus: RevisePass | null, onSelect: (id: string) => void): ReactNode[] {
  return styledPieces(text, marks.map((m) => m.range), (piece, k, a, b) => {
    const mark = marks.find((m) => m.range[0] <= a && m.range[1] >= b);
    const f = mark ? byId.get(mark.id) : undefined;
    if (!f) return <span key={k}>{piece}</span>;
    const faint = focus !== null && findingPass(f) !== focus;
    return (
      <a
        key={k}
        href={`finding://${encodeURIComponent(f.id)}`}
        className={`finding ${inkClass(f)}${faint ? ' faint' : ''}${selected === f.id ? ' chosen' : ''}${f.check.startsWith('rule:') ? ' rule' : ''}`}
        data-finding={f.id}
        data-check={f.check}
        title={f.checkName}
        onClick={(e) => {
          e.preventDefault();
          onSelect(f.id);
        }}
      >
        {piece}
      </a>
    );
  });
}

/** The pieces of a paragraph's Markdown between its spans' and these ranges' edges, styled; `wrap` draws each. */
function styledPieces(text: string, ranges: [number, number][], wrap: (piece: ReactNode, key: number, from: number, to: number) => ReactNode): ReactNode[] {
  const spans = markdown.scan(text);
  const cuts = new Set<number>([0, text.length]);
  for (const s of spans) { cuts.add(s.from); cuts.add(s.to); }
  for (const r of ranges) { cuts.add(r[0]); cuts.add(r[1]); }
  const points = [...cuts].filter((x) => x >= 0 && x <= text.length).sort((a, b) => a - b);
  const out: ReactNode[] = [];
  for (let k = 0; k + 1 < points.length; k++) {
    const a = points[k], b = points[k + 1];
    if (a === b) continue;
    const classes: string[] = [];
    let hidden = false;
    for (const s of spans) {
      if (s.from > a || s.to < b) continue;
      switch (s.kind) {
        case 'inlineMarker': hidden = true; break;
        case 'marker': case 'listMarker': case 'footnoteReference': classes.push('md-faint'); break;
        case 'italic': classes.push('i'); break;
        case 'bold': classes.push('b'); break;
        case 'smallCaps': classes.push('sc'); break;
        case 'underline': classes.push('u'); break;
        case 'highlight': if (s.ink) classes.push(`hl-${s.ink}`); break;
        case 'textColor': if (s.ink) classes.push(`c-${s.ink}`); break;
        default: break;
      }
    }
    if (hidden) continue;
    const piece = text.slice(a, b);
    out.push(wrap(classes.length ? <span className={classes.join(' ')}>{piece}</span> : piece, k, a, b));
  }
  return out;
}

/** The words around a finding with the passage marked (`mark`: underlined in the row, washed on the card). */
function Context({ finding, mark }: { finding: ReviseFinding; mark: 'underline' | 'wash' }) {
  const { text, match } = finding.context;
  return (
    <>
      {styledPieces(text, [match], (piece, k, a, b) =>
        a >= match[0] && b <= match[1] && match[1] > match[0] ? (
          <mark key={k} className={`context-match ${mark}`}>
            {piece}
          </mark>
        ) : (
          <span key={k}>{piece}</span>
        ),
      )}
    </>
  );
}

// MARK: The margin

/** The margin: the passes (All and each, with counts), the stepper, and the findings of the pass worked on, chapter by
 *  chapter; the chosen one is its card. */
function FindingsColumn({ findings, all, pass, loaded, styleChecked, selected, reveal, chapterTitle, onChoose, onStep, card }: {
  findings: ReviseFinding[]; all: ReviseFinding[]; pass: RevisePass | null; loaded: boolean; styleChecked: boolean; selected: string | null; reveal: number;
  chapterTitle(id: string): string; onChoose(f: ReviseFinding): void; onStep(forward: boolean): void; card(f: ReviseFinding): ReactNode;
}) {
  const list = useRef<HTMLDivElement>(null);
  const chapters = useMemo(() => {
    const out: { id: string; findings: ReviseFinding[] }[] = [];
    for (const f of findings) {
      const id = f.unitID ?? '';
      if (out.at(-1)?.id === id) out.at(-1)!.findings.push(f);
      else out.push({ id, findings: [f] });
    }
    return out;
  }, [findings]);
  // Keeps the chosen one in sight as the arrows step through.
  useEffect(() => {
    const box = list.current;
    const el = selected ? box?.querySelector<HTMLElement>(`[data-entry="${CSS.escape(selected)}"]`) : null;
    if (!box || !el) return;
    const top = el.offsetTop - box.offsetTop, bottom = top + el.offsetHeight;
    if (top < box.scrollTop || bottom > box.scrollTop + box.clientHeight) box.scrollTo({ top: Math.max(0, top - box.clientHeight * 0.2) });
  }, [reveal]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <aside className={`finding-column${pass ? ` pass-${pass}` : ''}`} aria-label="Findings">
      <PassSwitcher all={all} pass={pass} loaded={loaded} />
      <FindingStepper findings={findings} selected={selected} onStep={onStep} />
      <div className="finding-list" ref={list}>
        {findings.length === 0 ? (
          <div className="finding-empty">
            {!loaded ? (
              <p className="finding-hint" role="status">Reading the manuscript…</p>
            ) : pass ? (
              <>
                <p className="finding-hint">
                  {pass === 'style' && !styleChecked ? 'Switch on the style checks you want, then choose Check the Style in the column.' : `Nothing left in ${revisePassName(pass).toLowerCase()}.`}
                </p>
                {COLUMN_PASSES.filter((x) => x !== pass && all.some((f) => findingPass(f) === x)).map((x) => (
                  <button key={x} type="button" className={`link-button pass-link pass-${x}`} onClick={() => setChosenPass(x)}>
                    {revisePassName(x)}: {all.filter((f) => findingPass(f) === x).length} →
                  </button>
                ))}
              </>
            ) : (
              <p className="finding-hint">Nothing marked in the manuscript.</p>
            )}
          </div>
        ) : (
          chapters.map((c) => (
            <section key={c.id} className="finding-chapter" aria-label={chapterTitle(c.id)}>
              <h3 className="finding-chapter-head">
                <span className="eyebrow">{chapterTitle(c.id)}</span>
                <span className="column-count">{c.findings.length}</span>
              </h3>
              {c.findings.map((f) =>
                f.id === selected ? (
                  <div key={f.id} data-entry={f.id} className="finding-entry chosen">
                    {card(f)}
                  </div>
                ) : (
                  <button key={f.id} type="button" data-entry={f.id} className={`finding-row ${inkClass(f)}`} aria-label={`${f.checkName}: ${f.quote}`} onClick={() => onChoose(f)}>
                    <span className="finding-row-check">{eyebrow(f)}</span>
                    <span className="finding-row-text">
                      <Context finding={f} mark="underline" />
                    </span>
                  </button>
                ),
              )}
            </section>
          ))
        )}
      </div>
    </aside>
  );
}

/** The margin's passes: All and each pass with its count; the chosen one is what the margin lists and the stepper steps. */
function PassSwitcher({ all, pass, loaded }: { all: ReviseFinding[]; pass: RevisePass | null; loaded: boolean }) {
  const chip = (p: RevisePass | null, name: string, count: number) => (
    <button
      key={p ?? 'all'}
      type="button"
      className={`pass-chip${p ? ` pass-${p}` : ''}${pass === p ? ' on' : ''}`}
      aria-pressed={pass === p}
      aria-label={`${name}, ${count}`}
      data-pass-chip={p ?? 'all'}
      onClick={() => setChosenPass(p ?? 'all')}
    >
      {p ? <span className="pass-dot" aria-hidden="true" /> : null}
      {name}
      {loaded ? <span className="pass-chip-count">{count > 0 ? count.toLocaleString() : '✓'}</span> : null}
    </button>
  );
  return (
    <div className="pass-switcher" role="group" aria-label="Pass">
      {chip(null, 'All', all.length)}
      {COLUMN_PASSES.map((p) => chip(p, revisePassName(p), all.filter((f) => findingPass(f) === p).length))}
    </div>
  );
}

/** The phone's pass, as a menu in the stepper's pill. */
function PassMenu({ all, pass }: { all: ReviseFinding[]; pass: RevisePass | null }) {
  return (
    <MenuButton
      label={`Pass: ${pass ? revisePassName(pass) : 'All'}`}
      className={`quiet-button pass-menu${pass ? ` pass-${pass}` : ''}`}
      entries={() => [
        { kind: 'radio' as const, label: 'All', detail: String(all.length), checked: pass === null, onSelect: () => setChosenPass('all') },
        ...COLUMN_PASSES.map((p) => ({
          kind: 'radio' as const, label: revisePassName(p), detail: String(all.filter((f) => findingPass(f) === p).length), checked: pass === p, onSelect: () => setChosenPass(p),
        })),
      ]}
    >
      <span className="pass-dot" aria-hidden="true" />
      {pass ? revisePassName(pass) : 'All'}
    </MenuButton>
  );
}

/** "12 of 87" with ‹ ›: steps through the findings shown, across chapters. */
function FindingStepper({ findings, selected, onStep }: { findings: ReviseFinding[]; selected: string | null; onStep(forward: boolean): void }) {
  const index = selected ? findings.findIndex((f) => f.id === selected) : -1;
  return (
    <div className="finding-stepper">
      <span className="finding-stepper-count" aria-live="polite">
        {index >= 0 ? `${(index + 1).toLocaleString()} of ${findings.length.toLocaleString()}` : plural(findings.length, 'finding')}
      </span>
      <button type="button" className="stepper-button" aria-label="Previous finding" title="Previous finding" disabled={!findings.length || index === 0} onClick={() => onStep(false)}>
        ‹
      </button>
      <button type="button" className="stepper-button" aria-label="Next finding" title="Next finding" disabled={!findings.length || index === findings.length - 1} onClick={() => onStep(true)}>
        ›
      </button>
    </div>
  );
}

// MARK: The card

function FindingCard({ project, live, finding, shown, variants, canWrite, onClose }: PageProps & {
  finding: ReviseFinding; shown: ReviseFinding[]; variants: Record<string, VariantSet>; canWrite: boolean; onClose?: () => void;
}) {
  const ws = useWorkspace();
  const p = opened(project);
  const ref = useRef<HTMLElement>(null);
  useEffect(() => ref.current?.focus({ preventScroll: true }), [finding.id]);
  const rule = finding.check.startsWith('rule:');
  const set = finding.variantSetID ? variants[finding.variantSetID] : undefined;
  const chooser = set && variantChoosesAForm(set.kind) && set.kind !== 'numbers' ? set : null;
  const alike = shown.filter((f) => f.check === finding.check && f.replacement !== undefined).length;
  const short = [...finding.quote].length < 4 && finding.context.text.length > 0;
  const quote = [...finding.quote].length > 60 ? [...finding.quote].slice(0, 60).join('') + '…' : finding.quote;
  const leave = () => {
    leaveFinding(p, finding);
    announce('Left as it is.');
  };
  return (
    <section ref={ref} className={`finding-card ${inkClass(finding)}`} aria-label="Finding" data-finding-card={finding.id} tabIndex={-1}>
      {onClose ? (
        <button type="button" className="finding-close" aria-label="Close" onClick={onClose}>
          ×
        </button>
      ) : null}
      <p className="eyebrow finding-check">{eyebrow(finding)}</p>
      {finding.preview ? (
        <div className="finding-preview">
          <del>{finding.preview.before}</del>
          <ins>{finding.preview.after}</ins>
        </div>
      ) : short ? (
        <p className="finding-quote">
          <Context finding={finding} mark="wash" />
        </p>
      ) : (
        <p className="finding-quote">“{quote}”</p>
      )}
      <p className="finding-message">{finding.message}</p>
      {finding.check === SPELLING_CHECK ? (
        <SpellingActions project={project} live={live} finding={finding} canWrite={canWrite} onLeave={leave} />
      ) : chooser ? (
        <>
          <VariantChooser project={project} live={live} set={chooser} canWrite={canWrite} />
          <button type="button" className="link-button finding-quiet" onClick={leave}>
            Leave this one
          </button>
        </>
      ) : (
        <div className="finding-actions">
          {finding.replacement !== undefined ? (
            <button
              type="button"
              className="pill"
              disabled={!canWrite}
              title={`Make it “${plainText(finding.replacement).trim()}”`}
              onClick={() => {
                if (!live) return;
                chooseNext(p, finding);
                if (fixFinding(live, finding)) announce('Fixed.');
                else {
                  chooseFinding(project.id, finding.id, false);
                  announce('The text there has changed; check again.');
                }
              }}
            >
              Fix
            </button>
          ) : (
            <button
              type="button"
              className="pill"
              disabled={!canWrite}
              onClick={() => {
                if (!live) return;
                chooseNext(p, finding);
                const n = addToMargin(live, project.id, [finding]);
                announce(n ? `Added to the margin as a comment from “${finding.style ? 'Style linter' : finding.checkName}”.` : 'It’s in the margin already.');
              }}
            >
              Add to margin
            </button>
          )}
          <button type="button" className="pill outline" onClick={leave}>
            Leave it
          </button>
        </div>
      )}
      {finding.check === 'numbers' ? (
        <button type="button" className="link-button finding-also" disabled={!canWrite} title="Set the style sheet’s numbers to digits; these are no longer marked" onClick={() => live && setNumberStyle(live, 'digits')}>
          This Book Uses Digits
        </button>
      ) : null}
      {finding.replacement !== undefined && fixesAllAtOnce(finding.check) && alike > 1 ? (
        <button type="button" className="link-button finding-also" disabled={!canWrite} onClick={() => askFixAll(project.id, fixAllOf(finding.check, checkName(finding.check), shown))}>
          Fix All {alike.toLocaleString()} Like This…
        </button>
      ) : null}
      {rule ? <p className="finding-note">{PERSONAL_RULES_APPLE_ONLY}</p> : null}
      <button
        type="button"
        className="link-button finding-open"
        onClick={() => {
          const text = live?.text(finding.fragmentID) ?? p.fragmentRecord(finding.fragmentID)?.text ?? '';
          ws?.reveal({ fragmentId: finding.fragmentID, from: utf16OffsetOfScalar(text, finding.range[0]), to: utf16OffsetOfScalar(text, finding.range[1]) });
        }}
      >
        Open in Write →
      </button>
    </section>
  );
}

/** A word the spelling check doesn't know: the dictionary's guesses (the best filled), each making the change; Add to
 *  Glossary (the project's words, so it's known from now on); Leave it. Learn Word, for the writer's own dictionary, is on
 *  the Mac and iPad. */
function SpellingActions({ project, live, finding, canWrite, onLeave }: Pick<PageProps, 'project' | 'live'> & {
  finding: ReviseFinding; canWrite: boolean; onLeave(): void;
}) {
  const p = opened(project);
  const guesses = finding.guesses ?? [];
  return (
    <div className="spelling-actions">
      {guesses.length ? (
        <div className="finding-actions spelling-guesses" role="group" aria-label="Guesses">
          {guesses.map((g, i) => (
            <button
              key={g}
              type="button"
              className={`pill${i === 0 ? '' : ' outline'}`}
              disabled={!canWrite}
              title={`Make it “${g}”`}
              onClick={() => {
                if (!live) return;
                chooseNext(p, finding);
                if (fixFinding(live, { ...finding, replacement: g })) announce(`Changed to “${g}”.`);
                else {
                  chooseFinding(project.id, finding.id, false);
                  announce('The text there has changed; check again.');
                }
              }}
            >
              {g}
            </button>
          ))}
        </div>
      ) : (
        <p className="finding-note">No guesses.</p>
      )}
      <div className="finding-actions">
        <button
          type="button"
          className="pill outline"
          disabled={!canWrite}
          title={`Add “${finding.quote}” to this project’s glossary`}
          onClick={() => {
            if (!live) return;
            chooseNext(p, finding);
            addWordToGlossary(live, finding.quote);
            announce(`“${finding.quote}” is in the glossary: known from now on.`);
          }}
        >
          Add to Glossary
        </button>
        <button type="button" className="link-button finding-quiet" onClick={onLeave}>
          Leave it
        </button>
      </div>
      <p className="finding-note">Checked against SCOWL’s English dictionary. Learn Word, for your own dictionary, is on your Mac and iPad.</p>
    </div>
  );
}

/** A consistency set's forms, each with how often the book has it: choose one and use it throughout (the others change,
 *  after a checkpoint, and the choice goes in the style sheet), or keep both. */
function VariantChooser({ project, live, set, canWrite }: Pick<PageProps, 'project' | 'live'> & { set: VariantSet; canWrite: boolean }) {
  const { book } = useBookState(project.id);
  const [pick, setPick] = useState<string | null>(null);
  const chosen = pick ?? set.kept ?? set.forms[0];
  const changes = set.occurrences.filter((x) => x.form.toLowerCase() !== chosen.toLowerCase() && x.alternative !== null).length;
  return (
    <div className="variant-chooser">
      <div className="variant-forms" role="radiogroup" aria-label="Forms">
        {set.forms.map((form) => (
          <button key={form} type="button" role="radio" aria-checked={form === chosen} className={`variant-form${form === chosen ? ' on' : ''}`} onClick={() => setPick(form)}>
            <span className="variant-radio" aria-hidden="true" />
            <span className="variant-word">{form}</span>
            <span className="variant-count">{(set.counts[form] ?? 0).toLocaleString()}×</span>
          </button>
        ))}
      </div>
      <div className="finding-actions">
        <button
          type="button"
          className="pill"
          disabled={!canWrite || !book}
          onClick={() => {
            if (!live || !book) return;
            const n = applyVariant(live, project.id, book, set, chosen);
            announce(n ? `Changed ${plural(n, 'place')} to “${chosen}”. The style sheet keeps it.` : `The style sheet keeps “${chosen}”.`);
          }}
        >
          {changes > 0 ? `Use “${chosen}” · Change ${changes.toLocaleString()}` : `Use “${chosen}”`}
        </button>
        <button
          type="button"
          className="pill outline"
          disabled={!canWrite || !book}
          onClick={() => {
            if (!live || !book) return;
            applyVariant(live, project.id, book, set, null);
            announce('Both kept. The style sheet says so, so it won’t be raised again.');
          }}
        >
          Keep Both
        </button>
      </div>
      <p className="finding-note">
        {changes > 0 ? 'A checkpoint is recorded first. The choice goes in the style sheet, so a later one is caught.' : 'The choice goes in the style sheet, so a later one is caught.'}
      </p>
    </div>
  );
}

// MARK: Fix all and the style sheet

/** What a Fix all changes, check by check, and the checkpoint it records first. */
function FixAllDialog({ project, live, request, canWrite }: PageProps & { request: FixAllRequest; canWrite: boolean }) {
  const order = [...TYPOGRAPHY_CHECKS, ...MISTAKE_CHECKS, 'numbers'];
  const groups = new Map<string, { check: string; count: number }>();
  for (const f of request.findings) {
    const g = groups.get(f.checkName) ?? { check: f.check, count: 0 };
    g.count++;
    groups.set(f.checkName, g);
  }
  const rows = [...groups].sort((a, b) => {
    const i = order.indexOf(a[1].check), j = order.indexOf(b[1].check);
    return (i < 0 ? Infinity : i) - (j < 0 ? Infinity : j);
  });
  const close = () => askFixAll(project.id, null);
  return (
    <dialog
      className="confirm fix-all"
      aria-labelledby="fix-all-title"
      ref={(d) => {
        if (d && !d.open) d.showModal();
      }}
      onCancel={(e) => {
        e.preventDefault();
        close();
      }}
    >
      <h2 id="fix-all-title">{request.title}</h2>
      <table className="fix-all-table" aria-label="What will change">
        <tbody>
          {rows.map(([name, g]) => (
            <tr key={name}>
              <th scope="row">{name}</th>
              <td>{g.count.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="confirm-message small">
        A checkpoint, “{request.checkpoint}”, is recorded first, so History can put everything back. Passages you chose to leave, and locked documents, stay as
        they are.
      </p>
      <div className="actions">
        <button type="button" className="button secondary" onClick={close}>
          Cancel
        </button>
        <button
          type="button"
          className="button primary"
          autoFocus
          disabled={!canWrite || !request.findings.length}
          onClick={() => {
            close();
            if (!live) return;
            const n = fixAll(live, project.id, request.findings, request.checkpoint);
            announce(n ? `Fixed ${plural(n, 'place')}, after “${request.checkpoint}”.` : 'Nothing to fix: the text there has changed.');
          }}
        >
          Fix {request.findings.length.toLocaleString()}
        </button>
      </div>
    </dialog>
  );
}

/** "grey, not gray"; "Mr and Mr. both". */
const choiceText = (c: VariantChoice) =>
  c.keep === null ? `${c.forms.join(' and ')} both` : `${c.keep}, not ${c.forms.filter((f) => f.toLowerCase() !== c.keep!.toLowerCase()).join(', ')}`;

/** The project's style sheet: the dialogue marks, dash style and number style the checks hold the book to, and the
 *  consistency choices. Kept with the project, so every device checks against the same choices. */
function StyleSheetDialog({ project, live, canWrite }: PageProps & { canWrite: boolean }) {
  const sheet = projectStyleSheet(opened(project).record);
  const close = () => showStyleSheet(project.id, false);
  return (
    <dialog
      className="confirm style-sheet"
      aria-labelledby="style-sheet-title"
      ref={(d) => {
        if (d && !d.open) d.showModal();
      }}
      onCancel={(e) => {
        e.preventDefault();
        close();
      }}
    >
      <h2 id="style-sheet-title">Style Sheet</h2>
      <div className="style-sheet-fields">
        <label className="field-label stacked-label" htmlFor="sheet-dialogue">
          Dialogue in
        </label>
        <select id="sheet-dialogue" value={sheet.dialogueQuotes ?? 'auto'} disabled={!canWrite} onChange={(e) => live && setDialogueQuotes(live, e.target.value === 'auto' ? null : (e.target.value as 'double' | 'single'))}>
          <option value="auto">The marks the book mostly uses</option>
          <option value="double">Double quotes “ ”</option>
          <option value="single">Single quotes ‘ ’</option>
        </select>
        <p className="field-note">Speech in the other marks is listed under Typography ▸ Quotation marks for dialogue; quotes inside speech take the opposite marks.</p>
        <label className="field-label stacked-label" htmlFor="sheet-dashes">
          Dashes
        </label>
        <select id="sheet-dashes" value={sheet.dashStyle ?? 'auto'} disabled={!canWrite} onChange={(e) => live && setDashStyle(live, e.target.value === 'auto' ? null : (e.target.value as DashStyle))}>
          <option value="auto">The style the book mostly uses</option>
          {DASH_STYLES.map((s) => (
            <option key={s} value={s}>
              {dashStyleName(s)}: {dashStyleExample(s)}
            </option>
          ))}
        </select>
        <p className="field-note">Dashes between words are set this way under Typography ▸ Dash style. Ranges such as 1990–95 keep their en dash.</p>
        <label className="field-label stacked-label" htmlFor="sheet-numbers">
          Numbers
        </label>
        <select id="sheet-numbers" value={sheet.numbers ?? 'auto'} disabled={!canWrite} onChange={(e) => live && setNumberStyle(live, e.target.value === 'auto' ? null : (e.target.value as NumberStyle))}>
          <option value="auto">As the book mostly writes them</option>
          <option value="words">Spelled out to one hundred</option>
          <option value="digits">As digits</option>
        </select>
        <p className="field-note">Spelled out, digits to one hundred are marked under Consistency ▸ Numbers (times, dates, measurements and names aside).</p>
      </div>
      <h3 className="eyebrow style-sheet-choices-head">Choices</h3>
      {sheet.choices.length ? (
        <ul className="style-sheet-choices" aria-label="Choices">
          {sheet.choices.map((c) => (
            <li key={`${c.kind}:${c.forms.join('|')}`}>
              <span>
                {choiceText(c)}
                <span className="check-detail">{variantKindName(c.kind)}</span>
              </span>
              <button type="button" className="link-button" disabled={!canWrite} onClick={() => live && removeStyleChoice(live, c)}>
                Remove
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="confirm-message small">When Consistency finds a word written two ways, the one you choose is kept here.</p>
      )}
      <div className="actions">
        <button
          type="button"
          className="button secondary"
          title="Save the style sheet as a Markdown document: the house style, the choices, the cast’s names and the glossary’s words"
          onClick={() => {
            const p = opened(project);
            downloadText(`${p.record.title || 'Manuscript'} Style Sheet.md`, styleSheetDocument(p), 'text/markdown');
          }}
        >
          Export…
        </button>
        <button type="button" className="button primary" autoFocus onClick={close}>
          Done
        </button>
      </div>
    </dialog>
  );
}

// MARK: The column

export function ChecksColumn(props: PageProps) {
  const { project, live } = props;
  const ws = useWorkspace();
  const { p, book, loaded, all, pass, run } = useChecks(props);
  const settings = styleSettings(p.record);
  const switches = reviseSwitches();
  const sheet = projectStyleSheet(p.record);
  const canWrite = !!ws?.canWrite && !!live?.writable;
  const toggle = (check: string, on: boolean) => {
    if (isBuiltInStyleCheck(check)) {
      if (live) setBuiltInStyleCheck(live, check as BuiltInStyleCheck, !on);
    } else toggleCheck(check);
  };
  const dialogue = sheet.dialogueQuotes ? `Dialogue in ${sheet.dialogueQuotes === 'double' ? '“ ”' : '‘ ’'}` : 'Dialogue as the book has it';
  const dashes = sheet.dashStyle ? `dashes ${dashStyleExample(sheet.dashStyle)}` : 'dashes as the book has them';
  return (
    <div className="checks-column">
      <p className="column-note checks-lede">Work through the passes in order. Nothing changes your text unless you say so.</p>
      {COLUMN_PASSES.map((x) => {
        const here = all.filter((f) => findingPass(f) === x);
        const known = loaded && (x !== 'style' || run.findings !== null);
        const fixable = here.filter((f) => f.replacement !== undefined).length;
        return (
          <section key={x} className={`pass-section pass-${x}`} aria-label={revisePassName(x)} data-pass={x}>
            <button type="button" className={`pass-header${pass === x ? ' on' : ''}`} aria-pressed={pass === x} title={`Work on ${revisePassName(x).toLowerCase()} in the margin`} onClick={() => setChosenPass(x)}>
              <span className="pass-dot" aria-hidden="true" />
              <span className="pass-name">{revisePassName(x)}</span>
              {known ? here.length ? <span className="column-count">{here.length.toLocaleString()}</span> : <span className="pass-done" aria-label="Nothing found">✓</span> : null}
            </button>
            <p className="pass-summary">{passSummary(x)}</p>
            <ul className="column-list check-list" aria-label={`${revisePassName(x)} checks`}>
              {checksIn(x).map((c) => {
                if (c === SPELLING_CHECK && !spellingDictionary(p.record.locale)) {
                  return <CheckRow key={c} check={c} title={checkShortName(c)} on={false} count={0} detail="on your Mac and iPad" hint={`${checkName(c)}: ${checkSummary(c)} The web checks English; this project’s language is checked on your Mac and iPad.`} disabled onToggle={() => {}} />;
                }
                const on = isCheckOn(c, switches, settings);
                const exact = !isBuiltInStyleCheck(c) || styleCheckIsExact(c as BuiltInStyleCheck);
                if (c === SPELLING_CHECK) {
                  const failed = book?.spelling?.checked === false;
                  return (
                    <CheckRow
                      key={c}
                      check={c}
                      title={checkShortName(c)}
                      on={on}
                      count={on ? here.filter((f) => f.check === c).length : 0}
                      detail={failed ? 'the dictionary didn’t load' : undefined}
                      hint={`${checkName(c)}: ${checkSummary(c)} ${SPELLING_NOTE}${failed && book?.spelling?.error ? ` ${book.spelling.error}` : ''}`}
                      onToggle={() => toggle(c, on)}
                    />
                  );
                }
                return (
                  <CheckRow
                    key={c}
                    check={c}
                    title={checkShortName(c)}
                    on={on}
                    count={on ? here.filter((f) => f.check === c).length : 0}
                    detail={exact ? undefined : 'approximate here'}
                    hint={`${checkName(c)}: ${checkSummary(c)}${exact ? '' : ` ${APPROXIMATE_NOTE}`}`}
                    disabled={isBuiltInStyleCheck(c) && !canWrite}
                    onToggle={() => toggle(c, on)}
                  />
                );
              })}
              {x === 'style'
                ? settings.rules.map((r) => (
                    <CheckRow key={r.id} check={`rule:${r.id}`} title={r.name || 'Untitled habit'} rule on={r.enabled} count={0} detail="on your Mac and iPad" hint={PERSONAL_RULES_APPLE_ONLY} disabled onToggle={() => {}} />
                  ))
                : null}
            </ul>
            {x === 'typography' && fixable > 0 ? (
              <button type="button" className="pill outline pass-fix-all" disabled={!canWrite} title="Make every typography fix shown, after a checkpoint" onClick={() => askFixAll(project.id, fixAllTypography(all))}>
                Fix All {fixable.toLocaleString()}…
              </button>
            ) : null}
            {x === 'style' ? (
              <>
                <p className="column-note personal-note" data-apple-only>
                  Your own habits, taught by example, are checked on your Mac and iPad: they need Apple’s on-device language models.
                </p>
                <div className="check-run">
                  {run.progress ? (
                    <>
                      <p className="check-progress" role="status">{run.progress}</p>
                      <button type="button" className="pill outline" onClick={() => cancelStyleCheck(project.id)}>
                        Stop
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="pill outline check-manuscript"
                      disabled={!live || !settings.builtIns.size}
                      title={settings.builtIns.size ? 'Run the switched-on style checks over the whole manuscript' : 'Switch on a check first'}
                      onClick={() => live && void runStyleCheck(live, p)}
                    >
                      {run.findings === null ? 'Check the Style' : 'Check Again'}
                    </button>
                  )}
                  {run.error ? <p className="error" role="alert">{run.error}</p> : null}
                </div>
              </>
            ) : null}
          </section>
        );
      })}
      <div className="style-sheet-row">
        <p className="eyebrow">Style sheet</p>
        <p className="column-note">{dialogue}; {dashes}.</p>
        <button type="button" className="link-button" onClick={() => showStyleSheet(project.id, true)}>
          Edit Style Sheet…
        </button>
      </div>
    </div>
  );
}

const passSummary = (x: RevisePass) =>
  ({
    mistakes: 'Anything wrong. Checked as you go.',
    typography: 'Set as a book, not typed. Every fix is certain.',
    consistency: 'The book agreeing with itself.',
    style: 'Could it read better? Checked when you ask.',
  })[x];

function CheckRow({ check, title, on, count, detail, hint, rule, disabled, onToggle }: {
  check: string; title: string; on: boolean; count: number; detail?: string; hint: string; rule?: boolean; disabled?: boolean; onToggle(): void;
}) {
  return (
    <li>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        className={`check-row${rule ? ' rule' : ''}${on ? ' on' : ''}`}
        data-check={check}
        data-pass={rule ? 'rule' : checkPass(check)}
        title={hint}
        disabled={disabled}
        onClick={onToggle}
      >
        <span className="check-tick" aria-hidden="true" />
        <span className="check-title">
          {title}
          {detail ? <span className={`check-detail${detail === 'approximate here' ? ' approximate' : ''}`}>{detail}</span> : null}
        </span>
        {count > 0 ? <span className="column-count" aria-label={`${count} in the manuscript`}>{count.toLocaleString()}</span> : null}
      </button>
    </li>
  );
}

// MARK: The header's control: ‹ chapter ›

export function ChecksControls(props: PageProps) {
  const { project, hrefFor, params } = props;
  const p = opened(project);
  const units = useMemo(() => reviseUnits(p), [p]);
  const unit = reviseUnit(units, params.unit ?? null);
  if (!unit) return null;
  const index = units.findIndex((u) => u.node.id === unit.node.id);
  const hrefOf = (u: ArrangementTreeNode) => hrefFor('revise/checks', null, { unit: u.node.id });
  return (
    <div className="checks-controls">
      <nav className="chapter-nav" aria-label="Chapter">
        <a
          className={`chapter-step${index <= 0 ? ' disabled' : ''}`}
          href={index > 0 ? hrefOf(units[index - 1]) : undefined}
          aria-label="Previous chapter"
          aria-disabled={index <= 0}
          onClick={(e) => (index > 0 ? followLink(e, hrefOf(units[index - 1])) : e.preventDefault())}
        >
          ‹
        </a>
        <MenuButton
          label={`Chapter: ${unitTitle(p, unit)}`}
          className="quiet-button chapter-menu"
          entries={() => units.map((u) => ({ kind: 'radio' as const, label: unitTitle(p, u), checked: u.node.id === unit.node.id, onSelect: () => navigate(hrefOf(u)) }))}
        >
          {unitTitle(p, unit)}
        </MenuButton>
        <a
          className={`chapter-step${index + 1 >= units.length ? ' disabled' : ''}`}
          href={index + 1 < units.length ? hrefOf(units[index + 1]) : undefined}
          aria-label="Next chapter"
          aria-disabled={index + 1 >= units.length}
          onClick={(e) => (index + 1 < units.length ? followLink(e, hrefOf(units[index + 1])) : e.preventDefault())}
        >
          ›
        </a>
      </nav>
    </div>
  );
}

/** Whether a media query matches, following it. */
export function useNarrow(query: string): boolean {
  const [matches, setMatches] = useState(() => typeof matchMedia !== 'undefined' && matchMedia(query).matches);
  useEffect(() => {
    const m = matchMedia(query);
    const on = () => setMatches(m.matches);
    on();
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, [query]);
  return matches;
}
